import React, { useEffect, useState, useContext, useRef } from "react";
import { CombinedContext } from "../../../context/combinedContext";
import translations from "../../../translations/translations";
import companyBasedRequest from "../../../request/companyBasedRequest";
import "../adminModals.css";
import "./AdminServicesModal.css";
import { Edit, GripVertical, Plus, Trash2, Store, Home, Monitor, Clock } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import BannerMessage from "../../common/bannerMessage/bannerMessage";
import AdminServiceCategoriesModal from "../AdminServiceCategoriesModal/AdminServiceCategoriesModal";
import useModalDismiss from "../../common/useModalDismiss";

const DESCRIPTION_MAX = 150;

const AdminServicesModal = ({ isOpen, onClose, shopId, categories = [], initialServices = [], onOpenCategories, onSave, onSuccess }) => {
	useModalDismiss(onClose, { enabled: isOpen });
	const { language, userData, setUserData } = useContext(CombinedContext);

	const [saving, setSaving] = useState(false);
	const [invalidKeys, setInvalidKeys] = useState(new Set());
	const [bannerMessage, setBannerMessage] = useState("");
	const [showCategories, setShowCategories] = useState(false);

	const bodyRef = useRef(null);
	const categoryRefs = useRef({});
	const isAutoScrolling = useRef(false);
	const rowRefs = useRef({});
	const [activeCategoryId, setActiveCategoryId] = useState(null);

	const toParts = (totalMinutes = 0) => {
		const h = Math.floor((Number(totalMinutes) || 0) / 60);
		const m = (Number(totalMinutes) || 0) % 60;
		return { hours: h, minutes: m };
	};
	const toMinutes = (h = 0, m = 0) => Number(h) * 60 + Number(m);

	// Normalize any legacy boolean flag into the 3-state location model.
	const toLocationType = (s) => {
		if (s.locationType) return s.locationType;
		if (s.isHomeService) return "home";
		return "in_shop";
	};

	// Stable fingerprint of the shop services the rows were last built from.
	const servicesSignature = (list) =>
		JSON.stringify(
			(list || []).map((s) => [
				s.serviceId ?? null,
				s.name || s.labels?.[language] || "",
				s.description || "",
				Number(s.duration) || 0,
				s.price ?? 0,
				s.currency || "DOP",
				s.active !== false,
				toLocationType(s),
				s.transitMinutes ?? 30,
				s.categoryId || "cat_default",
			]),
		);

	const [rows, setRows] = useState([]);

	const hydratedFrom = useRef(null);

	useEffect(() => {
		// The parent refetches the whole shop after ANY save — including the categories
		// editor stacked on top of this modal — which hands us a new initialServices
		// array holding identical data. Re-mapping on identity alone would discard the
		// edits the user has not saved yet, so only rebuild when the data really changed.
		const signature = servicesSignature(initialServices);
		if (hydratedFrom.current === signature) return;
		hydratedFrom.current = signature;

		const mapped = (initialServices || []).map((s, idx) => {
			const { hours, minutes } = toParts(s.duration || 0);
			return {
				key: s.serviceId || `row_${idx}_${Date.now()}`,
				serviceId: s.serviceId,
				name: s.name || s.labels?.[language] || "",
				description: s.description || "",
				hours,
				minutes,
				price: s.price ?? 0,
				currency: s.currency || "DOP",
				active: s.active !== false,
				locationType: toLocationType(s),
				transitMinutes: s.transitMinutes ?? 30,
				categoryId: s.categoryId || "cat_default",
				_markedForDelete: false,
			};
		});

		setRows(
			mapped.length
				? mapped
				: [
						{
							key: `row_${Date.now()}`,
							serviceId: null,
							name: "",
							description: "",
							hours: 0,
							minutes: 30,
							price: "",
							currency: "DOP",
							active: true,
							locationType: "in_shop",
							transitMinutes: 30,
							categoryId: categories[0]?.categoryId || "cat_default",
							_markedForDelete: false,
						},
					],
		);
	}, [initialServices, language]);

	const hourOptions = Array.from({ length: 9 }, (_, i) => i);
	const minuteOptions = [0, 15, 30, 45];

	const updateRow = (idx, patch) => {
		setRows((prev) => {
			const next = [...prev];
			next[idx] = { ...next[idx], ...patch };
			return next;
		});
	};

	const addRow = (categoryId) => {
		setRows((prev) => [
			...prev,
			{
				key: `row_${Date.now()}`,
				serviceId: null,
				name: "",
				description: "",
				hours: 0,
				minutes: 30,
				price: "",
				currency: "DOP",
				active: true,
				locationType: "in_shop",
				transitMinutes: 30,
				categoryId: categoryId || categories[0]?.categoryId || "cat_default",
				_markedForDelete: false,
				_isNew: true, // 👈 permanent until modal closes
			},
		]);
	};

	const removeRow = (idx) => {
		setRows((prev) => {
			const next = [...prev];

			// If it's a NEW row (never saved), remove immediately
			if (!next[idx].serviceId) {
				next.splice(idx, 1);
				return next;
			}

			// Existing service → mark for deletion
			next[idx] = {
				...next[idx],
				_markedForDelete: true,
			};

			return next;
		});
	};

	const getDurationMinutes = (row) => toMinutes(row.hours, row.minutes);

	const isRowValid = (row) => {
		if (!row.name?.trim()) return false;
		if (row.price === "" || row.price == null || Number(row.price) < 0) return false;
		if (getDurationMinutes(row) <= 0) return false;

		// 🔥 Transit time only required for home ("A domicilio") services
		if (row.locationType === "home") {
			if (row.transitMinutes === "" || row.transitMinutes == null || Number(row.transitMinutes) <= 0) {
				return false;
			}
		}

		return true;
	};

	useEffect(() => {
		if (!bannerMessage) return;

		const hasInvalid = rows.filter((r) => !r._markedForDelete).some((r) => !isRowValid(r));

		if (!hasInvalid) {
			setBannerMessage("");
			setInvalidKeys(new Set());
		}
	}, [rows]);

	const handleSave = async () => {
		const activeRows = rows.filter((r) => !r._markedForDelete);

		// Validate
		const invalid = activeRows.filter((r) => !isRowValid(r));

		if (invalid.length > 0) {
			setInvalidKeys(new Set(invalid.map((r) => r.key)));
			setBannerMessage(translations[language]?.profile?.errors?.fillAllFields || "Please fill in all required fields.");
			return;
		}

		setInvalidKeys(new Set()); // clear errors
		const toDelete = rows.filter((r) => r._markedForDelete && r.serviceId);

		const toSave = rows.filter((r) => !r._markedForDelete);

		const payload = toSave.map((r) => {
			const locationType = r.locationType || "in_shop";

			return {
				serviceId: r.serviceId,
				name: r.name?.trim(),
				description: r.description?.trim(),
				duration: toMinutes(r.hours, r.minutes),
				price: r.price === "" ? 0 : Number(r.price),
				currency: r.currency || "DOP",
				active: r.active !== false,
				locationType,
				// Kept for backend backward-compatibility with the old boolean flag
				isHomeService: locationType === "home",
				transitMinutes: locationType === "home" ? Number(r.transitMinutes) : 0,
				categoryId: r.categoryId || "cat_default",
			};
		});

		try {
			setSaving(true);

			// 1️⃣ Delete removed services
			for (const r of toDelete) {
				await companyBasedRequest.removeShopService(shopId, r.serviceId, userData.user.userId, userData.cognitoTokens.AccessToken);
			}

			// 2️⃣ Save remaining services
			const res = await companyBasedRequest.updateShopServices(shopId, userData.user.userId, payload, userData.cognitoTokens.AccessToken);

			setUserData((prev) => ({
				...prev,
				shop: res.shop,
			}));

			onSuccess?.(language === "es" ? "¡Listo! Tus servicios se guardaron correctamente." : "All set! Your services have been saved.");
			onClose?.();
		} catch (e) {
			console.error("Failed to save services:", e);
			setBannerMessage(language === "es" ? "No pudimos guardar tus servicios. Inténtalo de nuevo." : "We couldn't save your services. Please try again.");
		} finally {
			setSaving(false);
		}
	};

	const visibleRows = rows.filter((r) => !r._markedForDelete);

	// key -> original rows index (so updateRow/removeRow edit the right row)
	const keyToRowsIndex = visibleRows.reduce((acc, r) => {
		const realIdx = rows.findIndex((x) => x.key === r.key);
		acc[r.key] = realIdx;
		return acc;
	}, {});

	// key -> draggable index (must be 0..N in the same order as visibleRows)
	const keyToDnDIndex = visibleRows.reduce((acc, r, i) => {
		acc[r.key] = i;
		return acc;
	}, {});

	// group for rendering (does NOT affect drag indexes)
	const grouped = categories.map((cat) => ({
		cat,
		items: visibleRows.filter((r) => (r.categoryId || "cat_default") === cat.categoryId),
	}));

	useEffect(() => {
		const container = bodyRef.current;
		if (!container) return;

		const handleScroll = () => {
			if (isAutoScrolling.current) return;

			const scrollTop = container.scrollTop;
			let current = null;

			Object.entries(categoryRefs.current).forEach(([catId, el]) => {
				if (!el) return;

				if (scrollTop >= el.offsetTop - 60) {
					current = catId;
				}
			});

			if (current && current !== activeCategoryId) {
				setActiveCategoryId(current);
			}
		};

		container.addEventListener("scroll", handleScroll, { passive: true });
		return () => container.removeEventListener("scroll", handleScroll);
	}, [activeCategoryId]);

	const t = translations[language]?.profile?.services || {};
	const profileT = translations[language].profile;

	const locationOptions = [
		{ value: "in_shop", label: t.inShop || (language === "es" ? "En el local" : "In shop"), Icon: Store },
		{ value: "home", label: t.homeService || (language === "es" ? "A domicilio" : "Home service"), Icon: Home },
		{ value: "online", label: t.online || "Online", Icon: Monitor },
	];

	return (
		<div className="sf-pm-overlay">
			<div className="sf-pm-modal is-wide">
				{/* Header */}
				<div className="sf-pm-header">
					<div className="sf-pm-heading">
						<h2 className="sf-pm-title">{t.title}</h2>
						<p className="sf-pm-sub">{profileT.modal.services.description}</p>
					</div>
					<button className="sf-pm-close" onClick={onClose} aria-label={profileT.actions.cancel}>
						✕
					</button>
				</div>

				{/* Body */}
				<div className="sf-pm-body is-flush" ref={bodyRef}>
					{/* STICKY CATEGORY TABS */}
					<div className="sf-pm-sv-tabbar">
						<div className="sf-pm-tabs">
							{categories.map((cat) => (
								<button
									key={cat.categoryId}
									type="button"
									className={`sf-pm-tab ${activeCategoryId === cat.categoryId ? "is-active" : ""}`}
									onClick={() => {
										const el = categoryRefs.current[cat.categoryId];
										const container = bodyRef.current;
										if (!el || !container) return;

										isAutoScrolling.current = true;
										setActiveCategoryId(cat.categoryId);

										container.scrollTo({
											top: el.offsetTop - 100, // offset for sticky tabs
											behavior: "smooth",
										});

										setTimeout(() => {
											isAutoScrolling.current = false;
										}, 350);
									}}
								>
									{cat.name?.[language]}
								</button>
							))}
						</div>

						<button
							className="sf-pm-sv-cat-edit"
							onClick={() => setShowCategories(true)}
							type="button"
							aria-label={profileT.modal.categories.title}
						>
							<Edit size={15} strokeWidth={1.9} />
						</button>
					</div>

					<div className="sf-pm-sv-list">
						<DragDropContext
							onDragEnd={(result) => {
								if (!result.destination) return;

								const updated = Array.from(rows);
								const [moved] = updated.splice(result.source.index, 1);
								updated.splice(result.destination.index, 0, moved);

								setRows(updated);
							}}
						>
							<Droppable droppableId="services">
								{(provided) => (
									<div ref={provided.innerRef} {...provided.droppableProps}>
										{grouped.map(({ cat, items }) => {
											return (
												<section
													key={cat.categoryId}
													ref={(el) => {
														if (el) categoryRefs.current[cat.categoryId] = el;
													}}
													className="sf-pm-sv-group"
												>
													<div className="sf-pm-sv-group-head">
														<p className="sf-pm-label">{cat.name?.[language]}</p>

														{items.length > 0 && (
															<span className="sf-pm-count">{items.length}</span>
														)}

														<button
															type="button"
															className="sf-pm-sv-add"
															onClick={() => addRow(cat.categoryId)}
														>
															{/* <Plus size={14} strokeWidth={2.4} /> */}
															{t.add || "Add"}
														</button>
													</div>

													{items.length === 0 && (
														<p className="sf-pm-empty">{t.emptyCategory}</p>
													)}

													{items.map((row) => {
														const realIdx = keyToRowsIndex[row.key]; // for updateRow/removeRow
														const dndIdx = keyToDnDIndex[row.key]; // for Draggable index
														const descLength = row.description?.length || 0;

														return (
															<Draggable key={row.key} draggableId={row.key.toString()} index={dndIdx}>
																{(dragProvided) => (
																	<article
																		className={`sf-pm-sv-card
																			${invalidKeys.has(row.key) ? "is-invalid" : ""}
																			${row._isNew && !invalidKeys.has(row.key) ? "is-new" : ""}`}
																		ref={(el) => {
																			dragProvided.innerRef(el);
																			if (el) rowRefs.current[row.key] = el;
																		}}
																		{...dragProvided.draggableProps}
																	>
																		{/* Card head */}
																		<header className="sf-pm-sv-card-head">
																			<span className="sf-pm-sv-card-label">{t.cardLabel || "Servicio"}</span>
																			<div className="sf-pm-sv-card-actions">
																				<button
																					type="button"
																					className="sf-pm-iconbtn is-danger"
																					onClick={() => removeRow(realIdx)}
																					aria-label="Delete service"
																				>
																					<Trash2 size={17} strokeWidth={1.8} />
																				</button>
																				<div
																					className="sf-pm-iconbtn sf-pm-sv-drag"
																					{...dragProvided.dragHandleProps}
																					aria-label="Reorder service"
																				>
																					<GripVertical size={17} strokeWidth={1.8} />
																				</div>
																			</div>
																		</header>

																		{/* Row 1: Name + Category */}
																		<div className="sf-pm-grid-2">
																			<div className="sf-pm-field">
																				<label className="sf-pm-field-label">{t.name}</label>
																				<input
																					className="sf-pm-input"
																					placeholder={t.placeholderName || "Service name"}
																					value={row.name}
																					onChange={(e) => updateRow(realIdx, { name: e.target.value })}
																				/>
																			</div>

																			<div className="sf-pm-field">
																				<label className="sf-pm-field-label">{t.category}</label>
																				<select
																					className="sf-pm-select"
																					value={row.categoryId || "cat_default"}
																					onChange={(e) => updateRow(realIdx, { categoryId: e.target.value })}
																				>
																					{categories.map((option) => (
																						<option key={option.categoryId} value={option.categoryId}>
																							{option.name?.[language] || "Unnamed"}
																						</option>
																					))}
																				</select>
																			</div>
																		</div>

																		{/* Row 2: Description */}
																		<div className="sf-pm-field">
																			<label className="sf-pm-field-label">{t.description}</label>
																			<div className="sf-pm-counted">
																				<input
																					className="sf-pm-input sf-pm-sv-desc"
																					placeholder={t.placeholderDescription || "Service description"}
																					value={row.description}
																					maxLength={DESCRIPTION_MAX}
																					onChange={(e) => updateRow(realIdx, { description: e.target.value })}
																				/>
																				<span
																					className={`sf-pm-counter sf-pm-sv-desc-count ${descLength >= DESCRIPTION_MAX - 10 ? "is-warn" : ""}`}
																				>
																					{descLength}/{DESCRIPTION_MAX}
																				</span>
																			</div>
																		</div>

																		{/* Row 3: Price + Hours + Minutes */}
																		<div className="sf-pm-sv-meta">
																			<div className="sf-pm-field">
																				<label className="sf-pm-field-label">
																					{t.price} ({row.currency || "DOP"})
																				</label>
																				<input
																					className="sf-pm-input"
																					inputMode="decimal"
																					pattern="[0-9]*"
																					placeholder="0"
																					value={row.price}
																					onChange={(e) => {
																						const v = e.target.value.replace(/[^\d]/g, "");
																						updateRow(realIdx, { price: v });
																					}}
																				/>
																			</div>

																			<div className="sf-pm-field">
																				<label className="sf-pm-field-label">{t.hours}</label>
																				<select
																					className="sf-pm-select"
																					value={row.hours}
																					onChange={(e) => updateRow(realIdx, { hours: Number(e.target.value) })}
																				>
																					{hourOptions.map((h) => (
																						<option key={h} value={h}>
																							{h}
																						</option>
																					))}
																				</select>
																			</div>

																			<div className="sf-pm-field">
																				<label className="sf-pm-field-label">{t.minutes}</label>
																				<select
																					className="sf-pm-select"
																					value={row.minutes}
																					onChange={(e) => updateRow(realIdx, { minutes: Number(e.target.value) })}
																				>
																					{minuteOptions.map((m) => (
																						<option key={m} value={m}>
																							{m}
																						</option>
																					))}
																				</select>
																			</div>
																		</div>

																		{/* Row 4: Location */}
																		<div className="sf-pm-field">
																			<label className="sf-pm-field-label">
																				{t.locationLabel || (language === "es" ? "Dónde se presta" : "Where it's provided")}
																			</label>
																			<div className="sf-pm-seg">
																				{locationOptions.map(({ value, label, Icon }) => (
																					<button
																						key={value}
																						type="button"
																						aria-pressed={row.locationType === value}
																						className={`sf-pm-seg-btn ${row.locationType === value ? "is-active" : ""}`}
																						onClick={() => updateRow(realIdx, { locationType: value })}
																					>
																						<Icon size={18} strokeWidth={1.8} />
																						<span>{label}</span>
																					</button>
																				))}
																			</div>

																			{row.locationType === "home" && (
																				<div className="sf-pm-sv-transit">
																					<span className="sf-pm-sv-transit-info">
																						<Clock size={16} strokeWidth={1.8} />
																						<span>
																							{t.transitTimeLabel ||
																								(language === "es"
																									? "Tiempo de traslado antes del servicio (min)"
																									: "Travel time before service (min)")}
																						</span>
																					</span>
																					<input
																						className="sf-pm-input sf-pm-sv-transit-input"
																						type="number"
																						inputMode="numeric"
																						min="0"
																						step="5"
																						value={row.transitMinutes}
																						onChange={(e) => updateRow(realIdx, { transitMinutes: e.target.value })}
																					/>
																				</div>
																			)}
																		</div>
																	</article>
																)}
															</Draggable>
														);
													})}
												</section>
											);
										})}

										{provided.placeholder}
									</div>
								)}
							</Droppable>
						</DragDropContext>
					</div>
				</div>

				{/* Footer */}
				<div className="sf-pm-footer">
					<button className="sf-pm-btn is-ghost" onClick={onClose} disabled={saving}>
						{translations[language].calendar.editor.cancel}
					</button>
					<button className="sf-pm-btn is-primary" onClick={handleSave} disabled={saving}>
						{saving && <span className="sf-pm-spinner" aria-hidden="true" />}
						{saving ? translations[language].calendar.editor.saving : translations[language].calendar.editor.save}
					</button>
				</div>
			</div>
			{bannerMessage && <BannerMessage type="error" text={bannerMessage} />}
			{showCategories && (
				<AdminServiceCategoriesModal
					isOpen={true}
					stacked
					language={language}
					shopId={shopId}
					initialCategories={categories}
					services={rows.filter((r) => !r._markedForDelete)}
					onClose={() => setShowCategories(false)}
					onSave={(updatedCategories) => {
						setShowCategories(false);
					}}
				/>
			)}
		</div>
	);
};

export default AdminServicesModal;
